import { useState } from 'react';
import type { FormEvent } from 'react';
import { AuthShell } from '@/components/auth/AuthShell';
import { FormField } from './Login';
import { useAuth } from '@/hooks/useAuth';

/**
 * Forgot password · 202 «pending_email» uniform response per ADR-004.
 * Per Stage C §2.1 / `_novice_register_first` · message must NOT confirm
 * whether email exists (account-enumeration safety).
 */

function emailLooksValid(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function translateAuthError(raw: string): string {
  if (/unable to validate email/i.test(raw)) return 'Email в неверном формате';
  if (/rate limit/i.test(raw)) return 'Слишком много попыток — попробуй через минуту';
  if (/network|fetch failed/i.test(raw)) return 'Не удалось связаться с сервером';
  return raw;
}

export interface ForgotPasswordScreenProps {
  onSwitchLogin: () => void;
}

export function ForgotPasswordScreen({ onSwitchLogin }: ForgotPasswordScreenProps) {
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!emailLooksValid(trimmed)) {
      setError('Введи корректный email');
      return;
    }
    setSubmitting(true);
    const res = await auth.forgotPassword(trimmed);
    setSubmitting(false);
    if ('error' in res) {
      setError(translateAuthError(res.error));
      return;
    }
    setSent(true);
  };

  if (sent) {
    return (
      <AuthShell
        title="Проверь почту"
        subtitle="Если email зарегистрирован — ссылка ушла. Кликни и установи новый пароль."
      >
        <div className="auth-pending">
          <div className="auth-pending__icon" aria-hidden="true">
            ✉
          </div>
          <p>Ссылка действует 1 час.</p>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onSwitchLogin}
          >
            ← Войти
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Восстановить доступ"
      subtitle="Введи email — пришлём ссылку для сброса пароля."
      crossLink={
        <span>
          Вспомнили?{' '}
          <button type="button" onClick={onSwitchLogin}>
            Войти
          </button>
        </span>
      }
    >
      <form className="auth-form" onSubmit={handleSubmit} noValidate data-testid="forgot-form">
        <FormField
          id="forgot-email"
          label="Email"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={email}
          onChange={setEmail}
        />
        <div className="auth-form__error-slot" aria-live="polite">
          {error && (
            <p className="auth-form__error" role="alert" data-testid="forgot-error">
              {error}
            </p>
          )}
        </div>
        <button
          type="submit"
          className="btn btn--primary auth-form__submit"
          disabled={submitting}
        >
          {submitting ? 'Отправляем…' : 'Прислать ссылку'}
        </button>
      </form>
    </AuthShell>
  );
}
